/**
 * In-memory de-duplication for the google-reviews edge function call.
 *
 * Both Index.tsx (for JSON-LD AggregateRating) and LiveReviewsSection
 * (for the visible carousel) need the same data. Without this cache,
 * two parallel network requests fire on initial load, lengthening the
 * critical request chain (see Lighthouse "Network dependency tree").
 *
 * This module returns the same Promise for concurrent callers and
 * caches the result for the lifetime of the page.
 */
// NOTE: The supabase client is imported dynamically inside `getReviews()`
// so the ~47 KiB of @supabase/auth-js, storage-js, postgrest-js and
// realtime-js code does not land in the main bundle. (Lighthouse "Reduce
// unused JavaScript" audit.) The client is only fetched when reviews are
// actually requested — well after first paint.

export interface ReviewItem {
  author: string;
  photo?: string | null;
  rating: number;
  text: string;
  publishTime: string | null;
  relativeTime: string;
}

export interface ReviewsPayload {
  placeName?: string;
  rating: number;
  totalReviews: number;
  newThisMonth?: number;
  googleMapsUri?: string;
  reviews: ReviewItem[];
  cachedAt?: string;
}

let inflight: Promise<ReviewsPayload | null> | null = null;
let resolved: ReviewsPayload | null = null;

export function getReviews(): Promise<ReviewsPayload | null> {
  if (resolved) return Promise.resolve(resolved);
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const { supabase } = await import("@/integrations/supabase/client");
      const { data, error } = await supabase.functions.invoke<ReviewsPayload>(
        "google-reviews",
        { method: "GET" },
      );
      if (error || !data) return null;
      resolved = data;
      return data;
    } catch {
      return null;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}
